import type { AuthTokens } from '@amai/contracts';
import { ApiRequestError } from './errors.js';

/**
 * Guarda dos tokens.
 *
 * Ficam em `sessionStorage`, não em `localStorage`: fechar a aba encerra a
 * sessão do operador, o que é o comportamento certo num computador
 * compartilhado de canteiro de obra. Quando a API passar a emitir cookie
 * httpOnly, este módulo é o único ponto a mudar.
 */
const ACCESS_KEY = 'amai.access';
const REFRESH_KEY = 'amai.refresh';

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    // Navegador com storage bloqueado: a sessão vive só em memória.
    return null;
  }
};

let memoryAccess: string | null = null;
let memoryRefresh: string | null = null;

export function getAccessToken(): string | null {
  return storage()?.getItem(ACCESS_KEY) ?? memoryAccess;
}

export function getRefreshToken(): string | null {
  return storage()?.getItem(REFRESH_KEY) ?? memoryRefresh;
}

export function setTokens(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'>): void {
  memoryAccess = tokens.accessToken;
  memoryRefresh = tokens.refreshToken;
  const store = storage();
  store?.setItem(ACCESS_KEY, tokens.accessToken);
  store?.setItem(REFRESH_KEY, tokens.refreshToken);
}

export function clearTokens(): void {
  memoryAccess = null;
  memoryRefresh = null;
  const store = storage();
  store?.removeItem(ACCESS_KEY);
  store?.removeItem(REFRESH_KEY);
}

export function hasSession(): boolean {
  return Boolean(getAccessToken() && getRefreshToken());
}

/**
 * Espera entre as tentativas de confirmar a sessão, em ms: três tentativas
 * a mais, ~12s no total, cobrem um soluço de rede e a API reiniciando num
 * deploy. Passou disso, a tela diz o que houve e oferece "Tentar de novo" —
 * sem apagar a sessão.
 */
export const ESPERAS_AO_CONFIRMAR = [1_000, 3_000, 8_000];

/**
 * A API disse que a sessão acabou. Só isso apaga os tokens.
 *
 * - `401`: sessão vencida, revogada ou usuário que não existe mais;
 * - `403`: no `/auth/me` só acontece com o usuário DESATIVADO — a rota não
 *   pede permissão nenhuma além de estar logado. Tratar como "servidor
 *   fora" deixaria quem foi desativado preso na tela de erro, tentando de
 *   novo para sempre.
 */
export function sessaoEncerrada(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.status === 401 || error.status === 403);
}
